/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Text, Pill } from '../components/ui';
import { Instagram, Facebook, X, Loader2 } from 'lucide-react';
import DisconnectConfirmModal from '../components/DisconnectConfirmModal';
import { useErrorModalStore } from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';
import {
  useDisconnectSocialAccount,
  useGetSocialAccounts,
  useConnectInstagram,
  useConnectFacebook,
} from '../queries/social';
import { useAppStore } from '../store/useAppStore';

const SocialsPage = ({ isOnboarding, currentStep, onFinish }) => {
  const queryClient = useQueryClient();
  const { openErrorModal } = useErrorModalStore();
  const { accessKey, setSocialAccounts, socialAccounts: storedSocialAccounts } = useAppStore();

  const [openDisconnectModal, setOpenDisconnectModal] = useState({
    isOpen: false,
    account: null,
  });

  const handleSocialAccountsError = useCallback((error) => {
    console.error('Failed to fetch social accounts:', error);
    if (error?.status >= 500) {
      return openErrorModal(getErrorModalMessage('server_error'), 'server_error');
    }
    openErrorModal(getErrorModalMessage('social_fetch_error'), 'social_fetch_error');
  }, [openErrorModal]);

  const handleSocialAccountsSuccess = useCallback((data) => {
    setSocialAccounts(data);
  }, [setSocialAccounts]);

  const shouldFetchSocialAccounts = useMemo(() => {
    if (!isOnboarding) {
      return !!accessKey && storedSocialAccounts === null;
    }
    return currentStep === 3 && !!accessKey && storedSocialAccounts === null;
  }, [isOnboarding, currentStep, accessKey, storedSocialAccounts]);

  console.log('SocialsPage - shouldFetchSocialAccounts:', shouldFetchSocialAccounts, 'currentStep:', currentStep);

  const { data: accounts = [], isLoading } = useGetSocialAccounts(
    shouldFetchSocialAccounts,
    handleSocialAccountsSuccess,
    handleSocialAccountsError
  );

  const { mutate: disconnectAccount, isPending: isLoadingDisconnect } =
    useDisconnectSocialAccount();

  const {
    mutate: connectInstagram,
    isPending: isConnectingInstagram,
    error: connectInstagramError,
  } = useConnectInstagram({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-accounts'] }),
  });
  const { mutate: connectFacebook, error: connectFacebookError } = useConnectFacebook({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-accounts'] }),
  });

  const handleDisconnect = (account) => {
    setOpenDisconnectModal({ isOpen: true, account });
  };

  const handleConfirmDisconnect = () => {
    if (disconnectAccount && openDisconnectModal.account) {
      disconnectAccount(openDisconnectModal.account.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
          setOpenDisconnectModal({ isOpen: false, account: null });
        },
        onError: () => {
          openErrorModal(
            getErrorModalMessage('social_disconnect_error'),
            'social_disconnect_error'
          );
          setOpenDisconnectModal({ isOpen: false, account: null });
        },
      });
    }
  };

  const handleCancelDisconnect = () => {
    setOpenDisconnectModal({ isOpen: false, account: null });
  };

  useEffect(() => {
    if (connectFacebookError || connectInstagramError) {
      openErrorModal(getErrorModalMessage('social_connect_error'), 'social_connect_error');
    }
  }, [connectFacebookError, connectInstagramError, openErrorModal]);

  if (isLoading || isLoadingDisconnect) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='w-10 h-10 animate-spin' style={{ color: 'var(--brand)' }} />
      </div>
    );
  }

  return (
    <div
      className={`${
        isOnboarding
          ? 'rounded-lg p-4 md:p-8 h-full md:h-auto overflow-auto'
          : 'space-y-8'
      }`}
      style={
        isOnboarding
          ? {
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }
          : {}
      }
    >
      {isOnboarding ? null : (
        <Text variant='heading' className='!text-2xl font-bold' style={{ color: 'var(--text-primary)' }}>
          Social Accounts
        </Text>
      )}

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        {/* Connect Social Accounts */}
        <div
          className={`${isOnboarding ? '' : 'rounded-lg p-8'}`}
          style={
            !isOnboarding
              ? {
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }
              : {}
          }
        >
          <Text variant='subtitle' className='text-lg font-bold mb-2' style={{ color: 'var(--text-primary)' }}>
            Connect Social Accounts
          </Text>
          <Text variant='body' className='text-sm mb-8' style={{ color: 'var(--text-muted)' }}>
            Connect your social media accounts to manage and schedule content across platforms.
          </Text>

          <div className='space-y-6'>
            {/* Instagram Business */}
            <div
              className='rounded-lg p-6 transition-shadow hover:shadow-md'
              style={{ border: '1px solid var(--border)' }}
            >
              <div className='flex items-start gap-4 mb-5'>
                <div className='w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shrink-0'>
                  <Instagram className='w-6 h-6 text-white' />
                </div>
                <div>
                  <Text variant='body' className='font-semibold' style={{ color: 'var(--text-primary)' }}>
                    Instagram Business
                  </Text>
                  <Text variant='caption' className='mt-1' style={{ color: 'var(--text-muted)' }}>
                    Connect your Instagram Business account to auto-publish posts and access
                    analytics.
                  </Text>
                </div>
              </div>
              <Button
                onClick={() => connectInstagram()}
                disabled={
                  accounts.find((account) => account.platform === 'instagram_business') ||
                  isConnectingInstagram
                }
                variant='primary'
                className='w-full'
              >
                {accounts.find((account) => account.platform === 'instagram_business')
                  ? 'Connected'
                  : 'Connect Instagram'}
              </Button>
            </div>

            {/* Facebook */}
            <div
              className='rounded-lg p-6 transition-shadow hover:shadow-md'
              style={{ border: '1px solid var(--border)' }}
            >
              <div className='flex items-start gap-4 mb-5'>
                <div className='w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center shrink-0'>
                  <Facebook className='w-6 h-6 text-white' />
                </div>
                <div>
                  <Text variant='body' className='font-semibold' style={{ color: 'var(--text-primary)' }}>
                    Facebook
                  </Text>
                  <Text variant='caption' className='mt-1' style={{ color: 'var(--text-muted)' }}>
                    Connect your Facebook page to schedule posts, manage comments, and track
                    performance.
                  </Text>
                </div>
              </div>
              <div className='relative group'>
                <Button
                  onClick={() => connectFacebook()}
                  variant='primary'
                  className='w-full'
                >
                  {accounts.find(
                    (account) => account.platform === 'facebook' || account.platform === 'instagram'
                  )
                    ? 'Connected'
                    : 'Connect Facebook'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Connected Accounts */}
        <div
          className={`${
            isOnboarding
              ? 'md:border-l pl-6'
              : 'rounded-lg p-8'
          } flex flex-col`}
          style={
            isOnboarding
              ? { borderColor: 'var(--border)' }
              : {
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                }
          }
        >
          {isOnboarding && accounts.length === 0 ? null : (
            <div className='mb-7'>
              <Text variant='subtitle' className='text-lg font-bold mb-2' style={{ color: 'var(--text-primary)' }}>
                Connected Accounts
              </Text>
              <Text variant='body' className='text-sm mb-6' style={{ color: 'var(--text-muted)' }}>
                Manage your connected social media accounts.
              </Text>
            </div>
          )}
          {accounts.length === 0 && !isOnboarding ? (
            <div className='flex-grow flex flex-col items-center justify-center text-center py-12 min-h-[300px]'>
              <div className='w-16 h-16 rounded-full flex items-center justify-center mb-4' style={{ background: 'var(--bg-surface)' }}>
                <span className='material-symbols-outlined text-4xl' style={{ color: 'var(--text-faint)' }}>
                  phonelink_off
                </span>
              </div>
              <Text variant='body' className='font-medium mb-1' style={{ color: 'var(--text-primary)' }}>
                No accounts connected
              </Text>
              <Text variant='body' className='text-sm max-w-xs mx-auto' style={{ color: 'var(--text-muted)' }}>
                Connect your social media accounts on the left to get started with Postreams.
              </Text>
            </div>
          ) : accounts.length === 0 && isOnboarding ? (
            <div className='flex-grow flex flex-col items-start justify-center gap-2 min-h-[300px]'>
              <Text variant='title' className='font-medium' style={{ color: 'var(--text-primary)' }}>
                Continue to Dashboard
              </Text>
              <Text variant='body' className='text-sm max-w-xs' style={{ color: 'var(--text-muted)' }}>
                You can connect social media accounts later from the dashboard.
              </Text>
              <Button onClick={onFinish} variant='primary' className='w-full'>
                Go to Dashboard &amp; Connect Later
              </Button>
            </div>
          ) : (
            <div className='space-y-6'>
              {isOnboarding && (
                <Button onClick={onFinish} variant='primary' className='w-full'>
                  Continue to Dashboard
                </Button>
              )}

              {accounts.map((account) => (
                <div
                  key={account.id}
                  className='flex items-center justify-between rounded-lg p-6 transition-shadow hover:shadow-md flex-wrap gap-4 xl:flex-nowrap'
                  style={{ border: '1px solid var(--border)' }}
                >
                  <div className='flex items-center gap-4'>
                    {account.platform === 'facebook' ? (
                      <div className='w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center shrink-0'>
                        <Facebook className='w-6 h-6 text-white' />
                      </div>
                    ) : (
                      <div className='w-12 h-12 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shrink-0'>
                        <Instagram className='w-6 h-6 text-white' />
                      </div>
                    )}

                    <div className='inline-flex flex-col'>
                      <Text variant='body' className='font-semibold' style={{ color: 'var(--text-primary)' }}>
                        {account.username || account.pageName || account.displayName}
                      </Text>
                      <Pill variant='warning' className='px-2 rounded-md !py-0.5 text-sm'>
                        {account.platform === 'instagram_business'
                          ? 'Instagram Business'
                          : 'Facebook'}
                      </Pill>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDisconnect(account)}
                    variant='primary'
                    size='md'
                    className='items-center w-full md:w-auto'
                    icon={X}
                    additionalIconClasses='w-5 h-5 mb-0.5'
                    iconPosition='left'
                  >
                    Disconnect
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <DisconnectConfirmModal
        isOpen={openDisconnectModal.isOpen}
        onClose={handleCancelDisconnect}
        onConfirm={handleConfirmDisconnect}
        account={openDisconnectModal.account}
      />
    </div>
  );
};

export default SocialsPage;
